import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import {
  fetchLivePsi,
  IS_DEBUG_MOCK,
  REGION_IDS,
  type PsiData,
  type RegionReading,
} from '../api/psi';
import { formatReadingTime, readingIsStale, type FeedStatus } from '../api/freshness';
import { describeTrend, fetchPsiTrend, mergeLatestReading, type TrendPoint } from '../api/trend';
import { useReducedMotion } from './useReducedMotion';

/** Own network requests, freshness, timers, and their cleanup for both designs. */
export function useAirQuality() {
  const liveData = ref<PsiData | null>(null);
  const isLoading = ref(true);
  const hasFeedError = ref(false);
  const now = ref(Date.now());
  const reducedMotion = useReducedMotion();
  const currentPsi = computed(() => liveData.value?.reading.psi ?? null);
  const currentPm25 = computed(() => liveData.value?.reading.pm25 ?? null);
  const currentTier = computed(() => liveData.value?.reading.tier ?? null);
  const displayedPsi = ref<number | null>(null);
  const emptyRegions: RegionReading[] = REGION_IDS.map((region) => ({ region, psi: null }));
  const regionalReadings = computed(() => liveData.value?.regions ?? emptyRegions);
  const feedStatus = computed<FeedStatus>(() => {
    if (!liveData.value) return isLoading.value ? 'loading' : 'unavailable';
    return hasFeedError.value || readingIsStale(liveData.value.timestamp, now.value)
      ? 'stale'
      : 'fresh';
  });
  const liveSourceLabel = computed(() => {
    if (IS_DEBUG_MOCK) return 'PREVIEW · MOCK DATA';
    if (feedStatus.value === 'stale') return 'NEA DATA · UPDATE DELAYED';
    if (isLoading.value) return liveData.value ? 'REFRESHING NEA DATA' : 'CONNECTING TO NEA';
    return feedStatus.value === 'fresh' ? 'NEA LIVE FEED · GOV.SG' : 'NEA DATA UNAVAILABLE';
  });
  const updatedLabel = computed(() =>
    liveData.value
      ? formatReadingTime(liveData.value.timestamp)
      : isLoading.value
        ? 'Awaiting first reading'
        : 'Official reading unavailable',
  );

  const history = ref<TrendPoint[]>([]);
  const isTrendLoading = ref(false);
  const hasTrendError = ref(false);
  const trendPartial = ref(false);
  const trendPoints = computed(() => mergeLatestReading(history.value, liveData.value, now.value));
  const trendSummary = computed(() => describeTrend(trendPoints.value));
  let refreshTimer = 0;
  let ageTimer = 0;
  let countFrame = 0;
  let lastAttempt = 0;
  let activeRequest: AbortController | undefined;
  let historyRequest: AbortController | undefined;

  function animateReading(target: number) {
    cancelAnimationFrame(countFrame);
    const start = displayedPsi.value;
    if (reducedMotion.value || start === null || Math.abs(target - start) <= 1) {
      displayedPsi.value = target;
      return;
    }
    const startedAt = performance.now();
    const step = (time: number) => {
      const progress = Math.min((time - startedAt) / 350, 1);
      displayedPsi.value = Math.round(start + (target - start) * (1 - Math.pow(1 - progress, 3)));
      if (progress < 1) countFrame = requestAnimationFrame(step);
    };
    countFrame = requestAnimationFrame(step);
  }
  watch(reducedMotion, (reduced) => {
    if (reduced) {
      cancelAnimationFrame(countFrame);
      displayedPsi.value = currentPsi.value;
    }
  });

  async function refreshTrend() {
    if (IS_DEBUG_MOCK) return;
    historyRequest?.abort();
    const request = new AbortController();
    historyRequest = request;
    isTrendLoading.value = true;
    try {
      const result = await fetchPsiTrend(Date.now(), request.signal);
      if (request.signal.aborted) return;
      history.value = result.points;
      trendPartial.value = result.partial;
      hasTrendError.value = false;
    } catch {
      if (!request.signal.aborted) hasTrendError.value = true;
    } finally {
      if (!request.signal.aborted) isTrendLoading.value = false;
    }
  }

  async function refresh() {
    activeRequest?.abort();
    const request = new AbortController();
    activeRequest = request;
    lastAttempt = Date.now();
    now.value = lastAttempt;
    isLoading.value = true;
    try {
      const data = await fetchLivePsi(request.signal);
      if (request.signal.aborted) return;
      liveData.value = data;
      hasFeedError.value = false;
      animateReading(data.reading.psi);
    } catch {
      if (!request.signal.aborted) hasFeedError.value = true;
    } finally {
      if (!request.signal.aborted) {
        isLoading.value = false;
        // History is independent: failures never hide a valid current reading.
        void refreshTrend();
      }
    }
  }
  function resume() {
    now.value = Date.now();
    if (document.visibilityState === 'visible' && now.value - lastAttempt >= 60 * 1000)
      void refresh();
  }
  onMounted(() => {
    refreshTimer = window.setInterval(
      () => {
        if (document.visibilityState === 'visible') void refresh();
      },
      15 * 60 * 1000,
    );
    ageTimer = window.setInterval(() => {
      now.value = Date.now();
    }, 60 * 1000);
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('online', resume);
    void refresh();
  });
  onBeforeUnmount(() => {
    activeRequest?.abort();
    historyRequest?.abort();
    window.clearInterval(refreshTimer);
    window.clearInterval(ageTimer);
    cancelAnimationFrame(countFrame);
    document.removeEventListener('visibilitychange', resume);
    window.removeEventListener('online', resume);
  });
  return {
    liveData,
    isLoading,
    hasFeedError,
    feedStatus,
    currentPsi,
    currentPm25,
    currentTier,
    displayedPsi,
    regionalReadings,
    liveSourceLabel,
    updatedLabel,
    reducedMotion,
    trendPoints,
    trendSummary,
    isTrendLoading,
    hasTrendError,
    trendPartial,
    refresh,
    refreshTrend,
  };
}
